// src/components/ConfidencePulseCard.jsx
// Shows confidence pulse history and score for a client

import React, { useState, useEffect, useCallback } from 'react';
import {
  Heart, Send, Clock, AlertTriangle, CheckCircle, TrendingUp,
  TrendingDown, Minus, ChevronDown, ChevronUp, Loader2, MessageSquare, RotateCcw
} from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';

// Get color scheme based on confidence pulse score (green/yellow/red)
// High confidence = green, moderate = yellow, low = red
function getConfidenceColors(confidenceScore) {
  if (confidenceScore === null || confidenceScore === undefined) {
    // Default/no data - neutral gray
    return {
      gradient: 'from-slate-400 to-slate-500',
      bg: 'bg-slate-400',
      text: 'text-slate-600',
      light: 'bg-slate-50',
      border: 'border-slate-200',
      button: 'bg-slate-500 hover:bg-slate-600'
    };
  }
  if (confidenceScore >= 80) {
    // High confidence - Green
    return {
      gradient: 'from-green-500 to-green-600',
      bg: 'bg-green-500',
      text: 'text-green-600',
      light: 'bg-green-50',
      border: 'border-green-200',
      button: 'bg-green-500 hover:bg-green-600'
    };
  }
  if (confidenceScore >= 60) {
    // Moderate confidence - Yellow
    return {
      gradient: 'from-yellow-500 to-yellow-600',
      bg: 'bg-yellow-500',
      text: 'text-yellow-600',
      light: 'bg-yellow-50',
      border: 'border-yellow-200',
      button: 'bg-yellow-500 hover:bg-yellow-600'
    };
  }
  // Low confidence - Red
  return {
    gradient: 'from-red-500 to-red-600',
    bg: 'bg-red-500',
    text: 'text-red-600',
    light: 'bg-red-50',
    border: 'border-red-200',
    button: 'bg-red-500 hover:bg-red-600'
  };
}

// Sentiment colors
const SENTIMENT_CONFIG = {
  positive: { bg: 'bg-green-100', text: 'text-green-700', label: 'Positive' },
  neutral: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Neutral' },
  negative: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Negative' },
  critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'Critical' }
};

// Confidence score color based on value
function getConfidenceColor(score) {
  if (score >= 80) return 'bg-green-500';
  if (score >= 60) return 'bg-yellow-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-red-500';
}

// Trend icon component
function TrendIcon({ trend }) {
  if (trend === 'improving') {
    return <TrendingUp className="w-4 h-4 text-green-600" />;
  }
  if (trend === 'declining') {
    return <TrendingDown className="w-4 h-4 text-red-600" />;
  }
  return <Minus className="w-4 h-4 text-gray-400" />;
}

export default function ConfidencePulseCard({ clientId, clientName }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [pulseData, setPulseData] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Get colors based on confidence score (will update after data loads)
  const confidenceScore = pulseData?.currentScore;
  const colors = getConfidenceColors(confidenceScore);

  // Load pulse data for this client
  const loadData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/confidence-pulse/client/${clientId}`, {
        credentials: 'include'
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const data = json.data;
      // Transform API response to component format
      const latestPulse = data.pulses?.[0];
      const lastSentAt = data.client?.lastPulseSentAt || latestPulse?.sentAt;
      const daysSince = lastSentAt
        ? Math.floor((new Date() - new Date(lastSentAt)) / (1000 * 60 * 60 * 24))
        : null;

      setPulseData({
        currentScore: data.client?.lastPulseScore ?? latestPulse?.confidenceScore ?? null,
        trend: data.client?.pulseTrend || 'stable',
        daysSinceLastPulse: daysSince,
        nextDue: data.client?.lastPulseSentAt
          ? new Date(new Date(data.client.lastPulseSentAt).getTime() + 90 * 24 * 60 * 60 * 1000)
          : null,
        pulses: data.pulses?.map(p => ({
          id: p.id,
          status: p.status,
          sentAt: p.sentAt,
          completedAt: p.completedAt,
          q1PlanConfidence: p.q1,
          q2AgentTrust: p.q2,
          q3WouldCallFirst: p.q3,
          openResponse: p.openResponse,
          confidenceScore: p.confidenceScore,
          sentiment: p.sentiment,
          detectedTopics: p.topics,
          requiresAction: p.requiresAction,
          actionReason: p.actionReason
        })) || []
      });
    } catch (err) {
      console.error('Failed to load confidence pulse data:', err);
      // Don't show error - pulse feature may just not be used yet
      setPulseData({ currentScore: null, pulses: [] });
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Send a new pulse
  const sendPulse = async () => {
    setSending(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${BASE}/confidence-pulse/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to send pulse');
      setSuccess('Pulse sent!');
      loadData();
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to send pulse');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSending(false);
    }
  };

  // Reset pulse data for testing
  const resetPulse = async () => {
    if (!window.confirm('Reset all pulse data for this client? This will clear all pulse history and reset the risk score to Stable.')) {
      return;
    }

    setResetting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE}/confidence-pulse/reset/${clientId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to reset');
      setSuccess('Pulse data reset!');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to reset pulse data');
      setTimeout(() => setError(''), 5000);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-100 p-6 h-full">
        <div className="flex items-center gap-2 mb-4">
          <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors.gradient} flex items-center justify-center text-white`}>
            <Heart size={18} />
          </span>
          <h3 className="font-bold text-lg text-[#172A3A]">Confidence Pulse</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className={`w-6 h-6 ${colors.text} animate-spin`} />
        </div>
      </div>
    );
  }

  const latestPulse = pulseData?.pulses?.[0];
  const hasScore = pulseData?.currentScore !== null && pulseData?.currentScore !== undefined;
  const confidenceColor = hasScore ? getConfidenceColor(pulseData.currentScore) : null;

  return (
    <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-lg border border-slate-100 p-6 hover:shadow-xl transition-shadow h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colors.gradient} flex items-center justify-center text-white`}>
            <Heart size={18} />
          </span>
          <h3 className="font-bold text-lg text-[#172A3A]">Confidence Pulse</h3>
        </div>
        <div className="flex items-center gap-2">
          {hasScore && (
            <button
              onClick={resetPulse}
              disabled={resetting}
              className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition disabled:opacity-50"
              title="Reset pulse data (for testing)"
            >
              {resetting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RotateCcw className="w-4 h-4" />
              )}
            </button>
          )}
          <button
            onClick={sendPulse}
            disabled={sending}
            className={`flex items-center gap-1.5 px-3 py-1.5 ${colors.button} text-white text-sm font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Send confidence check"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            Send Pulse
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          {success}
        </div>
      )}

      {/* Main Content */}
      {hasScore ? (
        <>
          {/* Score Display */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            {/* Confidence Score */}
            <div className="col-span-2 bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full ${confidenceColor} flex items-center justify-center`}>
                  <span className="text-xl font-bold text-white">{pulseData.currentScore}</span>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Confidence Score</p>
                  <div className="flex items-center gap-2 mt-1">
                    <TrendIcon trend={pulseData.trend} />
                    <span className={`text-sm font-medium ${
                      pulseData.trend === 'improving' ? 'text-green-600' :
                      pulseData.trend === 'declining' ? 'text-red-600' : 'text-gray-500'
                    }`}>
                      {pulseData.trend === 'improving' ? 'Improving' :
                       pulseData.trend === 'declining' ? 'Declining' : 'Stable'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Last Pulse */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500 mb-1">Last Pulse</p>
              <div className="flex items-center gap-1 text-slate-700">
                <Clock className="w-4 h-4" />
                <span className="font-medium text-sm">
                  {pulseData.daysSinceLastPulse !== null
                    ? `${pulseData.daysSinceLastPulse}d ago`
                    : 'N/A'}
                </span>
              </div>
              {pulseData.nextDue && (
                <p className="text-xs text-slate-500 mt-1">
                  Next: {new Date(pulseData.nextDue).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>

          {/* Latest Response Summary (if available) */}
          {latestPulse?.status === 'completed' && (
            <div className={`${colors.light} rounded-xl p-3 mb-3 border ${colors.border}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-700">Latest Response</p>
                {latestPulse.sentiment && (
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    SENTIMENT_CONFIG[latestPulse.sentiment]?.bg || 'bg-gray-100'
                  } ${SENTIMENT_CONFIG[latestPulse.sentiment]?.text || 'text-gray-700'}`}>
                    {SENTIMENT_CONFIG[latestPulse.sentiment]?.label || latestPulse.sentiment}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Plan Confidence</p>
                  <p className="font-medium capitalize text-sm">{latestPulse.q1PlanConfidence?.replace('_', ' ') || '--'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Agent Trust</p>
                  <p className="font-medium capitalize text-sm">{latestPulse.q2AgentTrust || '--'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Would Call First</p>
                  <p className="font-medium capitalize text-sm">{latestPulse.q3WouldCallFirst || '--'}</p>
                </div>
              </div>
              {latestPulse.openResponse && (
                <div className="mt-2 pt-2 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Client Comment</p>
                  <p className="text-sm text-slate-700 italic">"{latestPulse.openResponse}"</p>
                </div>
              )}
              {latestPulse.requiresAction && (
                <div className="mt-2 flex items-center gap-2 text-sm text-red-600">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">{latestPulse.actionReason}</span>
                </div>
              )}
            </div>
          )}

          {/* Expandable History */}
          {pulseData.pulses?.length > 1 && (
            <>
              <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-center gap-1 py-2 text-sm text-slate-500 hover:text-slate-700 transition"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    Hide History
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    Show History ({pulseData.pulses.length - 1} more)
                  </>
                )}
              </button>

              {expanded && (
                <div className="mt-2 space-y-2">
                  {pulseData.pulses.slice(1).map(pulse => (
                    <div
                      key={pulse.id}
                      className="bg-white rounded-lg border border-slate-200 p-3 text-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">
                          {new Date(pulse.completedAt || pulse.sentAt).toLocaleDateString()}
                        </span>
                        <div className="flex items-center gap-2">
                          {pulse.confidenceScore !== null && (
                            <span className="font-medium">{pulse.confidenceScore}</span>
                          )}
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            pulse.status === 'completed' ? 'bg-green-100 text-green-700' :
                            pulse.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            pulse.status === 'expired' ? 'bg-gray-100 text-gray-500' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {pulse.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      ) : (
        /* No Pulse Data Yet */
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
          <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600 font-medium">No confidence data yet</p>
          <p className="text-sm text-slate-400 mt-1">
            Send a pulse to check in with {clientName?.split(' ')[0] || 'this client'}
          </p>
        </div>
      )}
    </div>
  );
}
